import type { UserPortal } from '@branditdev/mf-types'
import { useMemo, useState } from 'react'
import { ShoppingCart, PieChart, CheckSquare } from 'react-feather'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import branditRails from '../../api/brandit_rails'

type AdminHeaderActionsProps = {
  currentPortalId?: number | null
  userPortals?: UserPortal[] | null
}

type StoreSsoUrlResponse = {
  url?: string | null
}

const AdminHeaderActions = ({ currentPortalId, userPortals }: AdminHeaderActionsProps) => {
  const location = useLocation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [openingPortalId, setOpeningPortalId] = useState<number | null>(null)

  const storePortals = useMemo(() => userPortals ?? [], [userPortals])

  const activePortalId = useMemo(() => {
    const pathSegments = location.pathname.split('/')
    const portalIdFromUrl = Number.parseInt(pathSegments[2], 10)
    const firstUserPortalId = storePortals[0]?.portal_id

    return Number.isInteger(portalIdFromUrl) ? portalIdFromUrl : currentPortalId || firstUserPortalId
  }, [currentPortalId, location.pathname, storePortals])

  const isReportsPage = location.pathname.includes('/reports')
  const reportsPath = isReportsPage || !activePortalId ? '/admin/reports' : `/admin/${activePortalId}/reports`
  const ordersPath = activePortalId ? `/admin/${activePortalId}/orders` : '/admin/orders'

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState)

  const handlePortalSelect = async (portal: UserPortal) => {
    setDropdownOpen(false)

    if (!portal.portal_id) return

    // Open synchronously from the click so browsers do not block the later SSO redirect.
    const storeWindow = window.open('', '_blank')
    setOpeningPortalId(portal.portal_id)

    try {
      const { data } = await branditRails.get<StoreSsoUrlResponse>('admin_user/store_sso_url', {
        params: { portal_id: portal.portal_id }
      })

      if (!data?.url) throw new Error('No store login URL returned.')

      if (storeWindow) {
        storeWindow.location.href = data.url
      } else {
        window.location.assign(data.url)
      }
    } catch {
      if (storeWindow) storeWindow.close()
      toast.error('Could not open store. Please try again.')
    } finally {
      setOpeningPortalId(null)
    }
  }

  return (
    <div className="admin-header-actions">
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle
          className="admin-header-actions__store-toggle"
          caret
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <ShoppingCart size={16} style={{ marginRight: '8px' }} />
          <span style={{ marginRight: '5px' }}>Go To Store</span>
        </DropdownToggle>
        <DropdownMenu>
          {storePortals.map(portal => (
            <DropdownItem
              key={portal.id}
              disabled={openingPortalId === portal.portal_id}
              onClick={() => handlePortalSelect(portal)}
            >
              {openingPortalId === portal.portal_id ? 'Opening Store...' : portal.portal_name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      <Link to={reportsPath} className="admin-header-actions__link">
        <PieChart size={16} style={{ marginRight: '8px' }} />
        Reports
      </Link>
      <Link to={ordersPath} className="admin-header-actions__link">
        <CheckSquare size={16} style={{ marginRight: '8px' }} />
        Order Approvals
      </Link>
    </div>
  )
}

export default AdminHeaderActions
